import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { api } from "../../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../../lib/apiErrorMessage";
import { useVoluntaryEmail } from "../../../lib/queries/voluntaryEmails";

export function VoluntaryEmailPanel({ admissionId }: { admissionId: string }) {
  const { t, i18n } = useTranslation();
  const query = useVoluntaryEmail(admissionId);
  const [downloading, setDownloading] = useState<number | null>(null);
  const [downloadError, setDownloadError] = useState<unknown>(null);
  const error = query.error ?? downloadError;
  return (
    <section className="space-y-4" aria-label={t("voluntary_email.evidence")}>
      <h2 className="font-semibold text-slate-900">{t("voluntary_email.unverified")}</h2>
      <p className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950">
        {t("voluntary_email.warning")}
      </p>
      {query.data?.origin === "mailgun_json" && query.data.provider_context && (
        <p>
          <Link
            className="focus-visible:ring-brand-500 inline-flex min-h-11 items-center underline focus-visible:ring-2"
            to={`/projects/${query.data.provider_context.project_id}`}
          >
            {t("voluntary_email.provider_project")}
          </Link>
        </p>
      )}
      {error ? (
        <div role="alert" className="text-red-800">
          {getApiErrorMessage(error, normalizeLang(i18n.resolvedLanguage))}
          {query.error && (
            <button
              type="button"
              className="ml-2 min-h-11 underline"
              onClick={() => void query.refetch()}
            >
              {t("voluntary_email.retry")}
            </button>
          )}
        </div>
      ) : null}
      {query.isPending && <p role="status">{t("voluntary_email.loading")}</p>}
      {query.data && (
        <>
          <h3 data-guide-target="email-preview" className="break-words font-medium">
            {query.data.subject || t("voluntary_email.no_subject")}
          </h3>
          <p className="whitespace-pre-wrap break-words rounded bg-slate-50 p-3 text-sm text-slate-800">
            {query.data.text_preview || t("voluntary_email.no_text")}
          </p>
          {query.data.text_truncated && (
            <p className="text-sm text-slate-600">{t("voluntary_email.truncated")}</p>
          )}
          <ul
            data-guide-target="email-evidence"
            className="divide-y divide-slate-200 rounded border border-slate-200"
          >
            {query.data.parts.map((part) => (
              <li
                key={part.part_index}
                className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0 break-words text-sm">
                  <p>
                    {part.part_index === 0
                      ? t("voluntary_email.original")
                      : part.declared_name || t("voluntary_email.attachment")}
                  </p>
                  <p className="text-slate-500">
                    {t(`voluntary_email.representations.${part.representation}`)} ·{" "}
                    {part.size_bytes.toLocaleString(i18n.resolvedLanguage)}{" "}
                    {t("voluntary_email.bytes")}
                  </p>
                </div>
                <button
                  type="button"
                  disabled={downloading !== null}
                  className="focus-visible:ring-brand-500 min-h-11 shrink-0 rounded border border-slate-300 px-3 py-2 text-sm focus-visible:ring-2 disabled:opacity-50"
                  onClick={async () => {
                    setDownloading(part.part_index);
                    setDownloadError(null);
                    try {
                      await api.download(
                        `/v1/voluntary-emails/${encodeURIComponent(admissionId)}/parts/${part.part_index}`,
                        `evidence-${part.part_index}.bin`,
                      );
                    } catch (error) {
                      setDownloadError(error);
                    } finally {
                      setDownloading(null);
                    }
                  }}
                >
                  {t(
                    downloading === part.part_index
                      ? "voluntary_email.downloading"
                      : "voluntary_email.download",
                  )}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
